import Module from '../../module';
import scroll from '../../../libs/scroll/scroll';
import gsap from 'gsap';

interface OptionsInterface {
	cssId: {
		anim?: {
			stockInfoQuoteHeaderCls: string;
			stockInfoQuoteQuoteDataCls: string;
		},

		quoteDataInnerLoadingCls?: string;
		stockInfoQuoteQuoteDataInnerCls?: string;
		stockInfoQuoteQuoteDataDisclaimerCls?: string;
		stockInfoQuoteQuoteDataButtonCls?: string;
		buttonPrimaryActiveCls?: string;
		stockInfoQuoteQuoteDataAreaCls?: string;
		quoteDataAreaActiveCls?: string;

		stockDataQuoteDataPriceCls?: string;
		stockDataQuoteDataVolumeCls?: string;
		stockDataQuoteDataTodayOpenCls?: string;
		stockDataQuoteDataPreviousCloseCls?: string;
		stockDataQuoteDataIntradayHighCls?: string;
		stockDataQuoteDataIntradayLowCls?: string;
		stockDataQuoteData52WeekHighCls?: string;
		stockDataQuoteData52WeekLowCls?: string;
		stockDataQuoteDataChangeCls?: string;
		stockDataQuoteDataChangePercentCls?: string;
		quoteDataRowItemValueErrorCls?: string;
		quoteDataRowItemValueSuccessCls?: string;
		stockInfoQuoteQuoteDataDisclaimerItemInnerCls?: string;
		stockInfoQuoteQuoteDataButtonsWrapperCls?: string;
		quoteDataButtonsWrapperLoadingCls?: string;
	},

	apiStockEndpoint?: string;

	slug: string
}

class Quote extends Module {

	publicVars: OptionsInterface;
	privateVars: {
		currentTicker: string | null;
		tickers: {
			plsrl: 'plsrl';
			plsrv: 'plsrv';
			psrhf: 'psrhf';
		},
		animating: boolean;
	} = {
		currentTicker: null,
		tickers: {
			plsrl: 'plsrl',
			plsrv: 'plsrv',
			psrhf: 'psrhf',
		},
		animating: true,
	}

	constructor(options: OptionsInterface) {
		super('Investors Stock Info Quote Module');

		this.publicVars = options;
	}

	startup() {
		this.msg('startup');

		scroll.init();
		scroll.registerAnimatedElement(this.publicVars.cssId.anim?.stockInfoQuoteHeaderCls || '', 'investors_stock_info_quote', { animCls: 'animate__zoomIn50P', once: true});
		scroll.registerAnimatedElement(this.publicVars.cssId.anim?.stockInfoQuoteQuoteDataCls || '', 'investors_stock_info_quote', { animCls: 'animate__fadeIn', once: true});

		this.getStockData();

	}

	bind() {
		this.msg('bind');

		const buttonsHtml = document.querySelectorAll<HTMLElement>(this.publicVars.cssId.stockInfoQuoteQuoteDataButtonCls || '');

		buttonsHtml.forEach(button => {
			if(button.classList.contains(this.publicVars.cssId.buttonPrimaryActiveCls?.substring(1) || '')) {
				this.privateVars.currentTicker = button?.dataset['ticker'] || 'plsrl';
			}

			button.addEventListener('click', e => {

				const target = e.currentTarget;

				if(target instanceof HTMLElement) {
					const ticker = target.dataset.ticker;

					if(ticker) {
						this.handleButtonClick(ticker);
					}
				}
			})
		});
	}

	handleButtonClick(ticker: string) {
		if(this.privateVars.animating) {
			return;
		}

		const currentAreaHtml = document.querySelector(`${this.publicVars.cssId.stockInfoQuoteQuoteDataAreaCls}-${this.privateVars.currentTicker}`) as HTMLElement;
		const nextAreaHtml = document.querySelector(`${this.publicVars.cssId.stockInfoQuoteQuoteDataAreaCls}-${ticker}`) as HTMLElement;

		if(!currentAreaHtml || !nextAreaHtml) {
			return;
		}

		if(this.privateVars.currentTicker === ticker) {
			return;
		}

		this.privateVars.animating = true;

		const currentButtonHtml = document.querySelector<HTMLElement>(`${this.publicVars.cssId.stockInfoQuoteQuoteDataButtonCls}-${this.privateVars.currentTicker}`) as HTMLElement;
		const nextButtonHtml = document.querySelector<HTMLElement>(`${this.publicVars.cssId.stockInfoQuoteQuoteDataButtonCls}-${ticker}`) as HTMLElement;

		currentButtonHtml.classList.remove(this.publicVars.cssId.buttonPrimaryActiveCls?.substring(1) || '');
		nextButtonHtml.classList.add(this.publicVars.cssId.buttonPrimaryActiveCls?.substring(1) || '');


		gsap.to(`${this.publicVars.cssId.stockInfoQuoteQuoteDataAreaCls}-${this.privateVars.currentTicker}`, {
			duration: 0.1,
			opacity: 0,
			onComplete: () => {
				currentAreaHtml.classList.remove(this.publicVars.cssId?.quoteDataAreaActiveCls?.substring(1) || '');
				nextAreaHtml.classList.add(this.publicVars.cssId?.quoteDataAreaActiveCls?.substring(1) || '');
				nextAreaHtml.style.opacity = '0';
				gsap.to(`${this.publicVars.cssId.stockInfoQuoteQuoteDataAreaCls}-${ticker}`, {
					duration: 0.2,
					opacity: 1,
					onComplete: () => {
						this.privateVars.currentTicker = ticker;
						this.privateVars.animating = false;
					}
				});
			}
		});
	}

	async getStockData() {

		const quoteDataInnerHtml = document.querySelector(this.publicVars.cssId.stockInfoQuoteQuoteDataInnerCls || '');

		const quoteDataDisclaimerHtml = document.querySelector(this.publicVars.cssId.stockInfoQuoteQuoteDataDisclaimerCls || '') as HTMLElement;

		if(!quoteDataInnerHtml || !this.publicVars.apiStockEndpoint || !quoteDataDisclaimerHtml) {
			return;
		}

		try {
			const result = await fetch(this.publicVars.apiStockEndpoint, {});

			if (!result.ok) {
				quoteDataInnerHtml.classList.remove(this.publicVars.cssId?.quoteDataInnerLoadingCls?.substring(1) || '');
				this.privateVars.animating = false;
				return;
			}

			const data: {
				data: {
					metadata: {
						current_time_canada: string,
					},
					plsrv: {
						price: string;
						volume_formatted: string;
						today_open_price: string;
						previous_close: string;
						intraday_high: string;
						intraday_low: string;
						fifty_two_week_high: string;
						fifty_two_week_low: string;
						change: string;
						change_percent: string;
					},
					plsrl: {
						price: string;
						volume_formatted: string;
						today_open_price: string;
						previous_close: string;
						intraday_high: string;
						intraday_low: string;
						fifty_two_week_high: string;
						fifty_two_week_low: string;
						change: string;
						change_percent: string;
					},
					psrhf: {
						price: string;
						volume_formatted: string;
						today_open_price: string;
						previous_close: string;
						intraday_high: string;
						intraday_low: string;
						fifty_two_week_high: string;
						fifty_two_week_low: string;
						change: string;
						change_percent: string;
					}
				}
			} = await result.json();

			quoteDataInnerHtml.classList.remove(this.publicVars.cssId?.quoteDataInnerLoadingCls?.substring(1) || '');

			quoteDataDisclaimerHtml.style.opacity = '1';

			const stockDataQuoteDataPricePlsrlHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteDataPriceCls}-${this.privateVars.tickers.plsrl}`);
			const stockDataQuoteDataVolumePlsrlHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteDataVolumeCls}-${this.privateVars.tickers.plsrl}`);
			const stockDataQuoteDataTodayOpenPlsrlHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteDataTodayOpenCls}-${this.privateVars.tickers.plsrl}`);
			const stockDataQuoteDataPreviousClosePlsrlHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteDataPreviousCloseCls}-${this.privateVars.tickers.plsrl}`);
			const stockDataQuoteDataIntradayHighPlsrlHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteDataIntradayHighCls}-${this.privateVars.tickers.plsrl}`);
			const stockDataQuoteDataIntradayLowPlsrlHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteDataIntradayLowCls}-${this.privateVars.tickers.plsrl}`);
			const stockDataQuoteData52WeekHighPlsrlHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteData52WeekHighCls}-${this.privateVars.tickers.plsrl}`);
			const stockDataQuoteData52WeekLowPlsrlHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteData52WeekLowCls}-${this.privateVars.tickers.plsrl}`);
			const stockDataQuoteDataChangePlsrlHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteDataChangeCls}-${this.privateVars.tickers.plsrl}`);
			const stockDataQuoteDataChangePercentPlsrlHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteDataChangePercentCls}-${this.privateVars.tickers.plsrl}`);


			if(stockDataQuoteDataPricePlsrlHtml) {
				stockDataQuoteDataPricePlsrlHtml.innerHTML = '£' + parseFloat(data.data.plsrl.price);
			}

			if(stockDataQuoteDataVolumePlsrlHtml) {
				stockDataQuoteDataVolumePlsrlHtml.innerHTML = data.data.plsrl.volume_formatted;
			}

			if(stockDataQuoteDataTodayOpenPlsrlHtml) {
				stockDataQuoteDataTodayOpenPlsrlHtml.innerHTML = '£' + parseFloat(data.data.plsrl.today_open_price);
			}

			if(stockDataQuoteDataPreviousClosePlsrlHtml) {
				stockDataQuoteDataPreviousClosePlsrlHtml.innerHTML = '£' + parseFloat(data.data.plsrl.previous_close);
			}

			if(stockDataQuoteDataIntradayHighPlsrlHtml) {
				stockDataQuoteDataIntradayHighPlsrlHtml.innerHTML = '£' + parseFloat(data.data.plsrl.intraday_high);
			}

			if(stockDataQuoteDataIntradayLowPlsrlHtml) {
				stockDataQuoteDataIntradayLowPlsrlHtml.innerHTML = '£' + parseFloat(data.data.plsrl.intraday_low);
			}

			if(stockDataQuoteData52WeekHighPlsrlHtml) {
				stockDataQuoteData52WeekHighPlsrlHtml.innerHTML = '£' + parseFloat(data.data.plsrl.fifty_two_week_high);
			}

			if(stockDataQuoteData52WeekLowPlsrlHtml) {
				stockDataQuoteData52WeekLowPlsrlHtml.innerHTML = '£' + parseFloat(data.data.plsrl.fifty_two_week_low);
			}

			if(stockDataQuoteDataChangePlsrlHtml) {
				const change = parseFloat(data.data.plsrl.change);
				let changeFormatted = '';

				if(change < 0) {
					changeFormatted = change.toString().replace('-', '-£');
					stockDataQuoteDataChangePlsrlHtml.classList.add(this.publicVars.cssId.quoteDataRowItemValueErrorCls?.substring(1) || '');
				} else {
					changeFormatted = '£' + change.toString();
					stockDataQuoteDataChangePlsrlHtml.classList.add(this.publicVars.cssId.quoteDataRowItemValueSuccessCls?.substring(1) || '');
				}

				stockDataQuoteDataChangePlsrlHtml.innerHTML = `${changeFormatted}`;
			}

			if(stockDataQuoteDataChangePercentPlsrlHtml) {
				const changePercent = parseFloat(data.data.plsrl.change_percent);

				if(changePercent < 0) {
					stockDataQuoteDataChangePercentPlsrlHtml.classList.add(this.publicVars.cssId.quoteDataRowItemValueErrorCls?.substring(1) || '');
				} else {
					stockDataQuoteDataChangePercentPlsrlHtml.classList.add(this.publicVars.cssId.quoteDataRowItemValueSuccessCls?.substring(1) || '');
				}

				stockDataQuoteDataChangePercentPlsrlHtml.innerHTML = parseFloat(data.data.plsrl.change_percent) + '%';
			}

			const stockDataQuoteDataPricePlsrvHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteDataPriceCls}-${this.privateVars.tickers.plsrv}`);
			const stockDataQuoteDataVolumePlsrvHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteDataVolumeCls}-${this.privateVars.tickers.plsrv}`);
			const stockDataQuoteDataTodayOpenPlrsvHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteDataTodayOpenCls}-${this.privateVars.tickers.plsrv}`);
			const stockDataQuoteDataPreviousClosePlrsvHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteDataPreviousCloseCls}-${this.privateVars.tickers.plsrv}`);
			const stockDataQuoteDataIntradayHighPlrsvHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteDataIntradayHighCls}-${this.privateVars.tickers.plsrv}`);
			const stockDataQuoteDataIntradayLowPlrsvHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteDataIntradayLowCls}-${this.privateVars.tickers.plsrv}`);
			const stockDataQuoteData52WeekHighPlrsvHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteData52WeekHighCls}-${this.privateVars.tickers.plsrv}`);
			const stockDataQuoteData52WeekLowPlrsvHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteData52WeekLowCls}-${this.privateVars.tickers.plsrv}`);
			const stockDataQuoteDataChangePlrsvHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteDataChangeCls}-${this.privateVars.tickers.plsrv}`);
			const stockDataQuoteDataChangePercentPlrsvHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteDataChangePercentCls}-${this.privateVars.tickers.plsrv}`);

			if(stockDataQuoteDataPricePlsrvHtml) {
				stockDataQuoteDataPricePlsrvHtml.innerHTML = '$' + parseFloat(data.data.plsrv.price);
			}

			if(stockDataQuoteDataVolumePlsrvHtml) {
				stockDataQuoteDataVolumePlsrvHtml.innerHTML = data.data.plsrv.volume_formatted;
			}

			if(stockDataQuoteDataTodayOpenPlrsvHtml) {
				stockDataQuoteDataTodayOpenPlrsvHtml.innerHTML = '$' + parseFloat(data.data.plsrv.today_open_price);
			}

			if(stockDataQuoteDataPreviousClosePlrsvHtml) {
				stockDataQuoteDataPreviousClosePlrsvHtml.innerHTML = '$' + parseFloat(data.data.plsrv.previous_close);
			}

			if(stockDataQuoteDataIntradayHighPlrsvHtml) {
				stockDataQuoteDataIntradayHighPlrsvHtml.innerHTML = '$' + parseFloat(data.data.plsrv.intraday_high);
			}

			if(stockDataQuoteDataIntradayLowPlrsvHtml) {
				stockDataQuoteDataIntradayLowPlrsvHtml.innerHTML = '$' + parseFloat(data.data.plsrv.intraday_low);
			}

			if(stockDataQuoteData52WeekHighPlrsvHtml) {
				stockDataQuoteData52WeekHighPlrsvHtml.innerHTML = '$' + parseFloat(data.data.plsrv.fifty_two_week_high);
			}

			if(stockDataQuoteData52WeekLowPlrsvHtml) {
				stockDataQuoteData52WeekLowPlrsvHtml.innerHTML = '$' + parseFloat(data.data.plsrv.fifty_two_week_low);
			}

			if(stockDataQuoteDataChangePlrsvHtml) {
				const change = parseFloat(data.data.plsrv.change);
				let changeFormatted = '';

				if(change < 0) {
					changeFormatted = change.toString().replace('-', '-$');
					stockDataQuoteDataChangePlrsvHtml.classList.add(this.publicVars.cssId.quoteDataRowItemValueErrorCls?.substring(1) || '');
				} else {
					changeFormatted = '$' + change.toString();
					stockDataQuoteDataChangePlrsvHtml.classList.add(this.publicVars.cssId.quoteDataRowItemValueSuccessCls?.substring(1) || '');
				}

				stockDataQuoteDataChangePlrsvHtml.innerHTML = `${changeFormatted}`;
			}

			if(stockDataQuoteDataChangePercentPlrsvHtml) {

				const changePercent = parseFloat(data.data.plsrv.change_percent);

				if(changePercent < 0) {
					stockDataQuoteDataChangePercentPlrsvHtml.classList.add(this.publicVars.cssId.quoteDataRowItemValueErrorCls?.substring(1) || '');
				} else {
					stockDataQuoteDataChangePercentPlrsvHtml.classList.add(this.publicVars.cssId.quoteDataRowItemValueSuccessCls?.substring(1) || '');
				}

				stockDataQuoteDataChangePercentPlrsvHtml.innerHTML = parseFloat(data.data.plsrv.change_percent) + '%';
			}


			const stockDataQuoteDataPricePsrhfHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteDataPriceCls}-${this.privateVars.tickers.psrhf}`);
			const stockDataQuoteDataVolumePsrhfHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteDataVolumeCls}-${this.privateVars.tickers.psrhf}`);
			const stockDataQuoteDataTodayOpenPsrhfHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteDataTodayOpenCls}-${this.privateVars.tickers.psrhf}`);
			const stockDataQuoteDataPreviousClosePsrhfHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteDataPreviousCloseCls}-${this.privateVars.tickers.psrhf}`);
			const stockDataQuoteDataIntradayHighPsrhfHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteDataIntradayHighCls}-${this.privateVars.tickers.psrhf}`);
			const stockDataQuoteDataIntradayLowPsrhfHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteDataIntradayLowCls}-${this.privateVars.tickers.psrhf}`);
			const stockDataQuoteData52WeekHighPsrhfHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteData52WeekHighCls}-${this.privateVars.tickers.psrhf}`);
			const stockDataQuoteData52WeekLowPsrhfHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteData52WeekLowCls}-${this.privateVars.tickers.psrhf}`);
			const stockDataQuoteDataChangePsrhfHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteDataChangeCls}-${this.privateVars.tickers.psrhf}`);
			const stockDataQuoteDataChangePercentPsrhfHtml = document.querySelector(`${this.publicVars.cssId.stockDataQuoteDataChangePercentCls}-${this.privateVars.tickers.psrhf}`);

			if(stockDataQuoteDataPricePsrhfHtml) {
				stockDataQuoteDataPricePsrhfHtml.innerHTML = '$' + parseFloat(data.data.psrhf.price);
			}

			if(stockDataQuoteDataVolumePsrhfHtml) {
				stockDataQuoteDataVolumePsrhfHtml.innerHTML = data.data.psrhf.volume_formatted;
			}

			if(stockDataQuoteDataTodayOpenPsrhfHtml) {
				stockDataQuoteDataTodayOpenPsrhfHtml.innerHTML = '$' + parseFloat(data.data.psrhf.today_open_price);
			}

			if(stockDataQuoteDataPreviousClosePsrhfHtml) {
				stockDataQuoteDataPreviousClosePsrhfHtml.innerHTML = '$' + parseFloat(data.data.psrhf.previous_close);
			}

			if(stockDataQuoteDataIntradayHighPsrhfHtml) {
				stockDataQuoteDataIntradayHighPsrhfHtml.innerHTML = '$' + parseFloat(data.data.psrhf.intraday_high);
			}

			if(stockDataQuoteDataIntradayLowPsrhfHtml) {
				stockDataQuoteDataIntradayLowPsrhfHtml.innerHTML = '$' + parseFloat(data.data.psrhf.intraday_low);
			}

			if(stockDataQuoteData52WeekHighPsrhfHtml) {
				stockDataQuoteData52WeekHighPsrhfHtml.innerHTML = '$' + parseFloat(data.data.psrhf.fifty_two_week_high);
			}

			if(stockDataQuoteData52WeekLowPsrhfHtml) {
				stockDataQuoteData52WeekLowPsrhfHtml.innerHTML = '$' + parseFloat(data.data.psrhf.fifty_two_week_low);
			}

			if(stockDataQuoteDataChangePsrhfHtml) {
				const change = parseFloat(data.data.psrhf.change);
				let changeFormatted = '';

				if(change < 0) {
					changeFormatted = change.toString().replace('-', '-$');
					stockDataQuoteDataChangePsrhfHtml.classList.add(this.publicVars.cssId.quoteDataRowItemValueErrorCls?.substring(1) || '');
				} else {
					changeFormatted = '$' + change.toString();
					stockDataQuoteDataChangePsrhfHtml.classList.add(this.publicVars.cssId.quoteDataRowItemValueSuccessCls?.substring(1) || '');
				}

				stockDataQuoteDataChangePsrhfHtml.innerHTML = `${changeFormatted}`;
			}

			if(stockDataQuoteDataChangePercentPsrhfHtml) {
				const changePercent = parseFloat(data.data.psrhf.change_percent);

				if(changePercent < 0) {
					stockDataQuoteDataChangePercentPsrhfHtml.classList.add(this.publicVars.cssId.quoteDataRowItemValueErrorCls?.substring(1) || '');
				} else {
					stockDataQuoteDataChangePercentPsrhfHtml.classList.add(this.publicVars.cssId.quoteDataRowItemValueSuccessCls?.substring(1) || '');
				}

				stockDataQuoteDataChangePercentPsrhfHtml.innerHTML = parseFloat(data.data.psrhf.change_percent) + '%';
			}

			const stockInfoQuoteQuoteDataDisclaimerItemInnerHtml = document.querySelector(this.publicVars.cssId.stockInfoQuoteQuoteDataDisclaimerItemInnerCls || '')

			if(stockInfoQuoteQuoteDataDisclaimerItemInnerHtml) {
				stockInfoQuoteQuoteDataDisclaimerItemInnerHtml.innerHTML = data.data.metadata.current_time_canada;
			}

			const buttonsWrapperHtml = document.querySelector(this.publicVars.cssId.stockInfoQuoteQuoteDataButtonsWrapperCls || '');

			if(buttonsWrapperHtml) {
				buttonsWrapperHtml.classList.remove(this.publicVars.cssId.quoteDataButtonsWrapperLoadingCls?.substring(1) || '');
			}

			this.privateVars.animating = false;



		} catch(e) {
			quoteDataInnerHtml.classList.remove(this.publicVars.cssId?.quoteDataInnerLoadingCls?.substring(1) || '');
			this.privateVars.animating = false;
			return;
		}
	}
}

export default Quote;